<!--
// app/components/pages/mercato/simulator/MercatoMarketTab.vue
// @component MercatoMarketTab - Displays all market players with search/filter and buy functionality
-->
<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import MercatoPlayerCard from "./MercatoPlayerCard.vue";

type Props = {
  players: MercatoPlayer[];
  currentSquad: MercatoPlayer[];
  isSquadFull: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  buyPlayer: [player: MercatoPlayer];
}>();

const { $t, $tn } = useI18n();

// Search and filter state
const searchTerm = ref("");
const selectedPosition = ref<string>("");
const debouncedSearchTerm = ref("");

// Debounce search input (300ms)
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
watch(searchTerm, newValue => {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    debouncedSearchTerm.value = newValue;
  }, 300);
});

// Cleanup debounce timer on component unmount
onBeforeUnmount(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    debounceTimeout = null;
  }
});

// Get unique positions for filter
const availablePositions = computed(() => {
  const positions = new Set(props.players.map(p => p.position));
  return Array.from(positions).sort();
});

// Check if player is already in squad
const isPlayerInSquad = (player: MercatoPlayer): boolean => {
  return props.currentSquad.some(p => p.id === player.id);
};

// Filtered players based on search and position
const filteredPlayers = computed(() => {
  let filtered = props.players;

  // Filter by search term
  if (debouncedSearchTerm.value) {
    filtered = filtered.filter(player => {
      const term = debouncedSearchTerm.value.toLowerCase();
      const fullName = player.name.toLowerCase();
      const position = $t(player.position).toLowerCase();
      const marketValue = $tn(player.marketValue)?.toString() || "";
      return (
        player.name.toLowerCase().includes(term) ||
        fullName.includes(term) ||
        position.includes(term) ||
        marketValue.includes(term)
      );
    });
  }

  // Filter by position
  if (selectedPosition.value) {
    filtered = filtered.filter(player => player.position === selectedPosition.value);
  }

  return filtered;
});

const handleBuyPlayer = (player: MercatoPlayer) => {
  if (!isPlayerInSquad(player) && !props.isSquadFull) {
    emit("buyPlayer", player);
  }
};

const clearFilters = () => {
  searchTerm.value = "";
  selectedPosition.value = "";
  debouncedSearchTerm.value = "";
};
</script>

<template>
  <div class="mercato-market-tab">
    <!-- Filters Section -->
    <div class="mercato-market-tab__filters">
      <!-- Search Input -->
      <div class="filter-group">
        <label for="player-search" class="filter-label">{{
          $t("mercatoSimulator.search.placeholder")
        }}</label>
        <input
          id="player-search"
          v-model="searchTerm"
          type="text"
          :placeholder="$t('mercatoSimulator.search.placeholder')"
          class="filter-input"
        />
      </div>

      <!-- Position Filter -->
      <div class="filter-group">
        <label for="position-filter" class="filter-label">{{ $t("mercatoSimulator.filter.position") }}</label>
        <select id="position-filter" v-model="selectedPosition" class="filter-select">
          <option value="">{{ $t("general.all.female") }}</option>
          <option v-for="position in availablePositions" :key="position" :value="position">
            {{ $t(position) }}
          </option>
        </select>
      </div>

      <!-- Clear Filters Button -->
      <button
        v-if="searchTerm || selectedPosition"
        type="button"
        class="btn-clear-filters"
        @click="clearFilters"
      >
        {{ $t("draft.player.filters.clean") }}
      </button>
    </div>

    <!-- Results Count -->
    <div class="mercato-market-tab__results-count">
      <p class="text-sm text-gray-400">
        {{ filteredPlayers.length }} {{ $t("menuItem.players").toLowerCase() }}
        <span v-if="isSquadFull" class="text-mercato ml-2">
          ({{ $t("mercatoSimulator.validation.maxPlayers") }})
        </span>
      </p>
    </div>

    <!-- Players Grid -->
    <div class="mercato-market-tab__grid">
      <MercatoPlayerCard
        v-for="player in filteredPlayers"
        :key="player.id"
        :player="player"
        mode="buy"
        :disabled="isSquadFull || isPlayerInSquad(player)"
        @buy="handleBuyPlayer"
      />

      <!-- Empty State -->
      <div v-if="filteredPlayers.length === 0" class="mercato-market-tab__empty">
        <p class="text-gray-400">{{ $t("shared.filters.noData") }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mercato-market-tab {
  @apply flex flex-col gap-4;
}

.mercato-market-tab__filters {
  @apply bg-gray-900 rounded-lg p-4 border border-gray-700 flex flex-wrap gap-4 items-end;
}

.filter-group {
  @apply flex flex-col gap-2 flex-1 min-w-[200px];
}

.filter-label {
  @apply text-xs text-gray-400 uppercase tracking-wide;
}

.filter-input,
.filter-select {
  @apply bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-mercato transition-colors;
}

.filter-input::placeholder {
  @apply text-gray-500;
}

.btn-clear-filters {
  @apply bg-gray-700 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-600 transition-colors;
}

.mercato-market-tab__results-count {
  @apply px-2;
}

.mercato-market-tab__grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto p-1;
}

.mercato-market-tab__empty {
  @apply col-span-full flex items-center justify-center py-12;
}
</style>
